import styles from './table-date-selection.module.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateSelectionEnum, Select, TextField, DateRangePicker, MenuItem } from '@frontend-workspace/react-shared-lib';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { addMonths, addYears, differenceInDays, differenceInMonths, differenceInYears } from 'date-fns';

/* eslint-disable-next-line */
export interface TableDateSelectionProps {
  dateRange?: DateRange<Date>;
  onSetDateRange?: (dateRange: DateRange<Date> | null) => void;
  supportFutureDates?: boolean;
}

export function TableDateSelection(props: TableDateSelectionProps) {
  const { t, i18n } = useTranslation();
  const getDateSelection = () => {
    if (!props.dateRange) return DateSelectionEnum.LastDay;
    const startDate = props.dateRange[0] || new Date();
    const endDate = props.dateRange[1] || new Date();
    if (differenceInDays(endDate, startDate) === 1) return startDate.getDate() === new Date().getDate() ? DateSelectionEnum.Today : DateSelectionEnum.LastDay;
    if (differenceInDays(endDate, startDate) === 7) return startDate.getDate() === new Date().getDate() ? DateSelectionEnum.NextWeek : DateSelectionEnum.LastWeek;
    if (addMonths(startDate, 1).getDate() === endDate.getDate() && differenceInMonths(endDate, startDate) === 1 && endDate.getMonth() === new Date().getMonth()) return DateSelectionEnum.LastMonth;
    if (addYears(startDate, 1).getDate() === endDate.getDate() && differenceInYears(endDate, startDate) === 1) return startDate.getDate() === new Date().getDate() ? DateSelectionEnum.NextYear : DateSelectionEnum.LastYear;
    return DateSelectionEnum.Custom;
  };

  const dateSelections = [
    //{ id: DateSelectionEnum.All, name: 'Alle' },
    props.supportFutureDates && { id: DateSelectionEnum.NextYear, name: t("simpleTable.nextYear") },
    props.supportFutureDates && { id: DateSelectionEnum.NextMonth, name: t("simpleTable.nextMonth") },
    props.supportFutureDates && { id: DateSelectionEnum.NextWeek, name: t("simpleTable.nextWeek") },
    props.supportFutureDates && { id: DateSelectionEnum.Today, name: t("simpleTable.today") },
    { id: DateSelectionEnum.LastDay, name: t("simpleTable.last24Hours") },
    { id: DateSelectionEnum.LastWeek, name: t("simpleTable.lastWeek") },
    { id: DateSelectionEnum.LastMonth, name: t("simpleTable.lastMonth") },
    { id: DateSelectionEnum.LastYear, name: t("simpleTable.lastYear") },
    { id: DateSelectionEnum.Custom, name: t("simpleTable.custom") },
  ].filter(Boolean) as { id: DateSelectionEnum, name: string }[];
  const handleDateSelection = (event: any) => {
    if (!props.onSetDateRange) { return; }
    const start = new Date();
    const endDate = new Date();
    switch (event.target.value) {
      case DateSelectionEnum.All:
        props.onSetDateRange(null);
        break;
      case DateSelectionEnum.Today:
        endDate.setDate(start.getDate() + 1);
        props.onSetDateRange([start, endDate]);
        break;
      case DateSelectionEnum.NextWeek:
        endDate.setDate(start.getDate() + 7);
        props.onSetDateRange([start, endDate]);
        break;
      case DateSelectionEnum.NextMonth:
        endDate.setMonth(start.getMonth() + 1);
        props.onSetDateRange([start, endDate]);
        break;
      case DateSelectionEnum.NextYear:
        endDate.setFullYear(start.getFullYear() + 1);
        props.onSetDateRange([start, endDate]);
        break;
      case DateSelectionEnum.LastDay:
        start.setDate(start.getDate() - 1);
        props.onSetDateRange([start, new Date()]);
        break;
      case DateSelectionEnum.LastWeek:
        start.setDate(start.getDate() - 7);
        props.onSetDateRange([start, new Date()]);
        break;
      case DateSelectionEnum.LastMonth:
        start.setMonth(start.getMonth() - 1);
        props.onSetDateRange([start, new Date()]);
        break;
      case DateSelectionEnum.LastYear:
        start.setFullYear(start.getFullYear() - 1);
        props.onSetDateRange([start, new Date()]);
        break;
      case DateSelectionEnum.Custom:
        props.onSetDateRange(null);
        break;
      default:
        break;
    }
    setDateSelection(event.target.value);
  };
  const [dateSelection, setDateSelection] = useState(getDateSelection());
  return (<>
    <div className="pe-3">
      <Select
        label={t("simpleTable.date")}
        displayEmpty
        value={dateSelection}
        sx={{ width: 150 }}
        onChange={handleDateSelection}>
        {dateSelections.map(d => <MenuItem value={d.id} key={d.id}>{d.name}</MenuItem>)}
      </Select>
    </div>
    {/* {dateSelection === DateSelectionEnum.Custom && */}
    <div className="pe-3">
      <DateRangePicker
        inputFormat={"dd.MM.yyyy"}
        mask='__.__.____'
        value={props.dateRange || [new Date(), new Date()]}
        onChange={(newValue) => {
          props.onSetDateRange && props.onSetDateRange(newValue);
        }}
        renderInput={(startProps: any, endProps: any) => (<>
          <TextField ref={startProps.inputRef} {...startProps.inputProps}
            label={t("simpleTable.from")}
            sx={{ width: 120 }}
          />
          <div><span className='mx-2'>-</span></div>
          <TextField ref={endProps.inputRef} {...endProps.inputProps}
            label={t("simpleTable.to")}
            sx={{ width: 120 }}
          />
        </>)}
      />
    </div>
    {/* } */}
  </>);
}

export default TableDateSelection;